<template>
  <div class="media-uploader" :class="computedClass">
    <BaseIsland :variant="props.variant" :with-shadow="false" class="media-uploader__uploader">
      <BaseFileUploader
        v-model="files"
        :accept="acceptMediaTypes"
        :max-size="maxMediaSize"
        :max-files="maxFilesCount"
        :readonly="props.readonly"
        :with-preview="false"
        :multiple="props.multiple"
        @update:model-value="updateSrc"
      />
      <div v-if="props.allowSwitchMediaType" class="media-uploader__uploader-preview-switch">
        <BaseTypography
          type="text-mini"
          width="auto"
          :weight="isPhoto ? '500' : '700'"
          :color="isPhoto ? 'var(--color-foreground-regular)' : 'var(--color-foreground-primary)'"
        >
          Видео
        </BaseTypography>
        <BaseSwitch v-model="isPhoto" :disabled="isSelectedMediaType" />
        <BaseTypography
          type="text-mini"
          width="auto"
          :weight="isPhoto ? '700' : '500'"
          :color="isPhoto ? 'var(--color-foreground-primary)' : 'var(--color-foreground-regular)'"
        >
          Фото
        </BaseTypography>
      </div>
      <div v-if="tempMediaUrls.length" class="media-uploader__uploader-preview">
        <div
          v-for="(mediaUrl, index) in tempMediaUrls"
          :key="mediaUrl"
          class="media-uploader__uploader-preview-item"
        >
          <BaseAspectRatio :height="1" :width="1">
            <BaseImage
              v-if="mediaTypeValue === MediaType.Image"
              :src="mediaUrl"
            />
            <MediaPlayer
              v-else-if="mediaTypeValue === MediaType.Video"
              :src="mediaUrl"
              size="mini"
              height="100%"
            />
          </BaseAspectRatio>
          <BaseButtonIcon
            icon-name="vuesax:close-circle-linear"
            icon-color="var(--color-foreground-inverted)"
            size="xs"
            form="circle"
            variant="transparent-inverted"
            class="media-uploader__uploader-preview-remove"
            @click="handleRemoveFile(index)"
          />
        </div>
        <BaseCheckbox v-if="props.withSlider" v-model="isCombineIntoSlider">
          Объединить в слайдер
        </BaseCheckbox>
        <BaseButton with-full @click="handleAddMedia">
          Добавить
        </BaseButton>
      </div>
    </BaseIsland>
  </div>
</template>

<script setup lang="ts">
import {
  IMAGE_ACCEPT,
  MAX_MEDIA_COUNT,
  MAX_MEDIA_IMAGE_SIZE,
  MAX_MEDIA_VIDEO_SIZE,
  MEDIA_ACCEPT,
  MediaType,
  VIDEO_ACCEPT,
} from '~/shared/const'
import {
  detectMediaTypeFromFile,
  filterValidMediaFiles,
  getLocalMediaUrl,
  isValidMediaFile,
  useAnimationShake,
  useNotificationStore,
} from '~/shared/lib'
import {
  BaseAspectRatio,
  BaseButton,
  BaseButtonIcon,
  BaseCheckbox,
  BaseFileUploader,
  BaseImage,
  BaseIsland,
  BaseSwitch,
  BaseTypography,
} from '~/shared/ui'
import MediaPlayer from './MediaPlayer.vue'

interface Props {
  modelValue?: string[]
  mediaType?: MediaType
  readonly?: boolean
  loading?: boolean
  variant?: 'secondary'
  withSlider?: boolean
  allowSwitchMediaType?: boolean
  acceptAllMediaTypes?: boolean
  notifyInvalidFiles?: boolean
  autoAdd?: boolean
  multiple?: boolean
  maxFiles?: number
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  mediaType: MediaType.Image,
  variant: 'secondary',
  withSlider: true,
  notifyInvalidFiles: true,
  acceptAllMediaTypes: false,
  autoAdd: false,
  multiple: true,
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: string[], mediaType: MediaType): void
}>()

const { shakeClass } = useAnimationShake(() => props.errorMessage)

const computedClass = computed(() => ({
  [shakeClass.value]: true,
  'media-uploader--error': props.errorMessage,
}))

const isCombineIntoSlider = defineModel<boolean>('isSlider', { default: false })
const isPhoto = ref(true)
const isSelectedMediaType = ref(false)
const mediaTypeValue = ref<MediaType>(props.mediaType)

watch(() => props.mediaType, (value: MediaType) => {
  if (!props.acceptAllMediaTypes) {
    mediaTypeValue.value = value
  }
}, { immediate: true })

watch(isPhoto, (value: boolean) => {
  if (props.acceptAllMediaTypes || !props.allowSwitchMediaType) {
    return
  }

  mediaTypeValue.value = value ? MediaType.Image : MediaType.Video
})

const files = ref<File[]>([])
const tempMediaUrls = ref<string[]>([])

const { notify } = useNotificationStore()

const acceptMediaTypes = computed(() => {
  if (props.acceptAllMediaTypes) {
    return MEDIA_ACCEPT
  }

  return mediaTypeValue.value === MediaType.Image ? IMAGE_ACCEPT : VIDEO_ACCEPT
})

const maxMediaSize = computed(() => {
  if (props.acceptAllMediaTypes) {
    return MAX_MEDIA_VIDEO_SIZE
  }

  return mediaTypeValue.value === MediaType.Image ? MAX_MEDIA_IMAGE_SIZE : MAX_MEDIA_VIDEO_SIZE
})

const maxFilesCount = computed(() => {
  if (!props.multiple) {
    return 1
  }

  return props.maxFiles ?? MAX_MEDIA_COUNT
})

async function updateSrc(selectedFiles: File[]) {
  if (selectedFiles.length === 0) {
    return
  }

  const filesToValidate = selectedFiles.slice(0, maxFilesCount.value)
  let validFiles: File[] = []

  if (props.acceptAllMediaTypes) {
    validFiles = await filterValidMediaFiles(filesToValidate)
  }
  else {
    for (const file of filesToValidate) {
      const isValid = await isValidMediaFile(file, mediaTypeValue.value)

      if (isValid) {
        validFiles.push(file)
        continue
      }

      if (props.notifyInvalidFiles) {
        notify({
          title: 'Неподдерживаемый файл',
          message: `Файл «${file.name}» не соответствует выбранному типу медиа`,
          type: 'warning',
        })
      }
    }
  }

  if (validFiles.length === 0) {
    files.value = []
    tempMediaUrls.value = []
    return
  }

  const resolvedMediaType = props.acceptAllMediaTypes
    ? await detectMediaTypeFromFile(validFiles[0])
    : mediaTypeValue.value

  if (!resolvedMediaType) {
    files.value = []
    tempMediaUrls.value = []
    return
  }

  isSelectedMediaType.value = true
  mediaTypeValue.value = resolvedMediaType
  files.value = validFiles
  tempMediaUrls.value = validFiles.map(file => getLocalMediaUrl(file))

  if (props.autoAdd) {
    handleAddMedia()
  }
}

function handleAddMedia() {
  emit('update:modelValue', tempMediaUrls.value, mediaTypeValue.value)
}

function handleRemoveFile(index: number) {
  files.value.splice(index, 1)
  tempMediaUrls.value.splice(index, 1)
}
</script>

<style lang="scss" scoped>
.media-uploader {
  &__loading {
    height: inherit;

    &-skeleton {
      height: inherit;
      display: flex;
      flex-direction: column;
      gap: rem(16px);
      justify-content: center;
      align-items: center;
    }
  }

  &__uploader {
    $length: 5;
    $gap: 10px;

    display: flex;
    flex-direction: column;
    gap: rem(20px);

    &-preview {
      display: flex;
      flex-wrap: wrap;
      gap: rem($gap);

      > * {
        flex: 1 1 100%;
      }

      &-item {
        position: relative;
        border-radius: var(--radius-xs);
        overflow: hidden;
        flex: 0 1 calc(100% / $length - rem(($gap * ($length - 1)) / $length));
      }

      &-remove {
        position: absolute;
        top: rem(6px);
        right: rem(6px);
      }

      &-switch {
        display: flex;
        align-items: center;
        gap: rem(10px);
      }
    }
  }

  &--error {
    .media-uploader__uploader {
      border: 2px solid var(--color-foreground-danger);
    }
  }
}
</style>
